import * as React from 'react';
import { useIntl } from 'react-intl';
import {
  activityLabel,
  formatDuration,
  isToolPhase,
  localeOf,
  summarizeEntry,
  summaryParts,
  type EntrySummary,
} from './copilotActivity';

/**
 * Ligne de bilan sous une réponse du copilote (UPT-2041, lot 1) : « ✓ 2 lectures Géode · 4,1 s · 2 520 jetons ».
 *
 *  - une ligne grise qui passe à la ligne sur mobile ; sans `usage` du serveur, seulement les étapes et la durée ;
 *    sans outil, durée et jetons ; jetons estimés : « ≈ » devant le nombre et info-bulle « estimation » ;
 *  - le chevron « Détail » déplie les étapes (verbe, libellé d'entité, compte, durée ; une étape en erreur en
 *    couleur d'état) — jamais le nom brut de l'outil ni une valeur. Replié par défaut, l'état n'est pas mémorisé ;
 *  - une réponse interrompue porte le même bilan, partiel (jetons inconnus).
 * Les étapes ne vivent qu'en mémoire de session : une conversation rechargée n'a pas de bilan détaillé.
 */

const SEPARATOR = ' · '; // i18n-exempt: séparateur typographique, pas du texte

export interface CopilotSummaryLineProps {
  summary: EntrySummary;
}

export function CopilotSummaryLine({ summary }: CopilotSummaryLineProps) {
  const intl = useIntl();
  const detailId = React.useId();
  const [open, setOpen] = React.useState(false);
  const t = React.useCallback(
    (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values),
    [intl],
  );

  const aggregate = summarizeEntry(summary);
  const parts = summaryParts(aggregate, t, localeOf(intl));
  const lead = [parts.steps, parts.duration].filter((part) => part !== '').join(SEPARATOR);
  if (lead === '' && parts.tokens === '') {
    return null;
  }
  const steps = summary.items.filter((item) => isToolPhase(item.phase));

  let iconClass = 'fa fa-check text-success-text';
  if (summary.interrupted) {
    iconClass = 'fa fa-ban';
  } else if (aggregate.errors > 0) {
    iconClass = 'fa fa-exclamation-triangle text-warning-text';
  }

  return (
    <div className="px-1 text-xs text-portal-ink-muted">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <i className={iconClass} aria-hidden="true" />
        <p className="min-w-0">
          {lead}
          {parts.tokens !== '' ? (
            <>
              {lead !== '' ? SEPARATOR : null}
              <span title={parts.estimated ? t('copilot.summary.estimate') : undefined}>{parts.tokens}</span>
              {parts.estimated ? <span className="sr-only">{t('copilot.summary.estimate')}</span> : null}
            </>
          ) : null}
        </p>
        {steps.length > 0 ? (
          <button
            type="button"
            aria-expanded={open}
            aria-controls={detailId}
            onClick={() => setOpen((value) => !value)}
            className="inline-flex items-center gap-1 rounded hover:text-portal-ink hover:underline pointer-coarse:min-h-11"
          >
            <span>{t('copilot.summary.detail')}</span>
            <i className={open ? 'fa fa-chevron-up' : 'fa fa-chevron-down'} aria-hidden="true" />
          </button>
        ) : null}
      </div>
      {open && steps.length > 0 ? (
        <ul id={detailId} className="mt-1 space-y-0.5 ps-5">
          {steps.map((step) => (
            <li
              key={step.seq}
              className={`flex items-baseline justify-between gap-2 ${step.status === 'error' ? 'text-danger-text' : ''}`}
            >
              <span className="min-w-0 flex-1 break-words">{activityLabel(step, t)}</span>
              {step.durationMs !== undefined ? (
                <span className="shrink-0 tabular-nums">{formatDuration(step.durationMs, localeOf(intl))}</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export default CopilotSummaryLine;
